import { useEffect, useRef, useState } from "react"
import clsx from "clsx"
import Link from "next/link"
import Slider, { CustomArrowProps, Settings } from "react-slick"
import { useMedia } from "react-use"
import Image3 from "./hero/bg-hero-components.webp"
import Image2 from "./hero/bg-hero-fabrics.webp"
import Image1 from "./hero/bg-hero-online-ordering.webp"
import Image5 from "./hero/bg-hero-pos.webp"
import Image4 from "./hero/bg-hero-tooling.webp"
import S from "./hero.module.scss"
import ArrowLeftIcon from "../../svgs/feather-arrow-left.svg"
import ArrowRightIcon from "../../svgs/feather-arrow-right.svg"

const slides = [
  {
    background: Image1,
    title: "Online Ordering Just Got Easier",
    content: "<p>Custom Pricing Now Live</p>",
    button: {
      url: "/components",
      title: "Place order",
    },
  },
  {
    background: Image2,
    title: "Fabrics",
    content: "<p>Order fabrics from all our collections.</p>",
    button: {
      url: "/fabrics",
      title: "View products",
    },
  },
  {
    background: Image3,
    title: "Components",
    content:
      "<p>Order all the parts you need to make blinds and shutters..</p>",
    button: {
      url: "/components",
      title: "View products",
    },
  },
  {
    background: Image4,
    title: "Tooling",
    content:
      "<p>Order the tooling required to assemble your blinds & shutters.</p>",
    button: {
      url: "/tooling",
      title: "View products",
    },
  },
  {
    background: Image5,
    title: "Point of Sale",
    content:
      "<p>Order pattern books, brochures, leaflets and other promotional literature</p>",
    button: {
      url: "/point-of-sale",
      title: "View products",
    },
  },
]

const ArrowLeft = (props: CustomArrowProps) => {
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{ ...props.style }}
      className={clsx(props.className, S.arrow, S.left)}
      aria-label="Previous"
    >
      <ArrowLeftIcon />
    </button>
  )
}

const ArrowRight = (props: CustomArrowProps) => {
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{ ...props.style }}
      className={clsx(props.className, S.arrow, S.right)}
      aria-label="Next"
    >
      <ArrowRightIcon />
    </button>
  )
}

const IndexHero = () => {
  const slidesRef = useRef<HTMLDivElement[]>([])
  const [current, setCurrent] = useState(0)
  const reducedMotion = useMedia("(prefers-reduced-motion: reduce)", false)

  // gsap is loaded on demand so it stays out of the homepage bundle; the slide
  // markup renders server-side either way, only the fade-in waits on the chunk.
  const slideAnimation = async (slideNumber = 0) => {
    const slide = slidesRef.current[slideNumber]

    if (!slide || reducedMotion) return

    const { default: gsap } = await import("gsap")
    const slideTl = gsap.timeline()

    slideTl.fromTo(
      slide.querySelector("[data-gsap-title]"),
      { autoAlpha: 0, y: -25 },
      { autoAlpha: 1, y: 0 },
    )

    slideTl.fromTo(
      slide.querySelector("[data-gsap-content]"),
      { autoAlpha: 0, y: 25 },
      { autoAlpha: 1, y: 0 },
    )

    slideTl.fromTo(
      slide.querySelector("[data-gsap-button]"),
      { autoAlpha: 0, y: 5 },
      { autoAlpha: 1, y: 0 },
    )
  }

  const settings: Settings = {
    nextArrow: <ArrowRight />,
    prevArrow: <ArrowLeft />,
    infinite: false,
    autoplay: !reducedMotion,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    pauseOnFocus: true,
    beforeChange: (_: number, nextSlide: number) => {
      setCurrent(nextSlide)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      slideAnimation(nextSlide)
    },
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    slideAnimation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Slider {...settings} className={clsx(S.slider, "equal")}>
      {slides.map((item, index) => {
        const Title = index === 0 ? "h1" : "h2"

        return (
          <div key={item.title}>
            <div
              ref={(el) => {
                if (el) {
                  slidesRef.current[index] = el
                }
              }}
              className={S.slide}
              style={{ backgroundImage: `url(${item.background.src})` }}
            >
              <div className="container">
                <Title data-gsap-title className={S.title}>
                  {item.title}
                </Title>
                {item.content && (
                  <div
                    data-gsap-content
                    className={clsx(S.content, "wysiwyg")}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                )}
                {item.button && (
                  <div data-gsap-button className="text-center mt-4 mt-md-5">
                    <Link
                      href={item.button.url}
                      className="btn btn-primary"
                      tabIndex={index === current ? undefined : -1}
                    >
                      {item.button.title}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </Slider>
  )
}

export default IndexHero
